import { forwardRef, useCallback, useEffect } from 'react'
import {
  AvatarUploadField,
  AvatarUploadFieldProps,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import {
  Maybe,
  UpdateProfileInput,
  UserConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type Props = {
  user: Maybe<UserConnectionFragment>
  onSubmitMyProfile: (inputs: UpdateProfileInput) => Promise<void>
  onClickAvatar: AvatarUploadFieldProps['onClick']
}

export type MyProfileValues = {
  avatarPath: string | null
  displayName: string
  accountId: string
  introduction: string
}

const schema: UseFormProps['schema'] = o => ({
  avatarPath: o
    .string()
    .optional()
    .allow('', null)
    .label(tLabel('myProfileEdit.avatarPath')),
  displayName: o.string().max(50).label(tLabel('myProfileEdit.displayName')),
  accountId: o
    .string()
    .pattern(new RegExp(/^[a-zA-Z0-9_]*$/))
    .max(20)
    .label(tLabel('myProfileEdit.accountId')),
  introduction: o
    .string()
    .max(150)
    .optional()
    .allow('')
    .label(tLabel('myProfileEdit.introduction')),
})

/**
 *  MyProfileEdit Form Organism.
 *
 *  This component is organism layer but is named suffix xxForm,
 *  because all of form components belong to Organism.
 */
export const MyProfileEditForm: Organism<Props> = ({
  user,
  onSubmitMyProfile,
  onClickAvatar,
}) => {
  const { t } = useTranslation('myProfileEdit')
  const { showMessage, showError } = useMessage()
  const {
    handleSubmit,
    formState: { errors },
    register,
    reset,
    setValue,
    setError,
  } = useForm<MyProfileValues>({
    schema,
    defaultValues,
  })

  useEffect(() => {
    if (!user) {
      return
    }
    reset({
      displayName: user.displayName ?? '',
      avatarPath: null,
      introduction: user.introduction ?? '',
      accountId: user.accountId ?? '',
    })
  }, [user, reset])

  const onSubmit = handleSubmit(values =>
    onSubmitMyProfile(values)
      .then(() => {
        setValue('avatarPath', null, { shouldValidate: true })
        showMessage(t('submitSuccess'), 'success')
      })
      .catch(error => {
        handleError<MyProfileValues>(error, setError, showError)
      })
  )

  const handleOnClick = useCallback(
    async function onClick() {
      const result = await onClickAvatar()
      if (!result) {
        return
      }
      setValue('avatarPath', result?.tmpPath ?? null, {
        shouldValidate: true,
      })
      return result
    },
    [onClickAvatar, setValue]
  )

  return (
    <Form id="my-profile-edit-form" onSubmit={onSubmit} sx={{ pt: 3 }}>
      <AvatarUploadCell
        onClick={handleOnClick}
        avatarProps={{ src: user?.avatar?.smallUri ?? '' }}
        titleProps={{ color: 'primary.main', sx: { pt: 2 } }}
        type="image"
        error={errors?.avatarPath}
        {...register('avatarPath')}
      />
      <TextInputCell
        type="text"
        error={errors?.displayName}
        {...register('displayName')}
      />
      <TextInputCell
        type="text"
        error={errors?.accountId}
        {...register('accountId')}
      />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        error={errors?.introduction}
        {...register('introduction')}
      />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('myProfileEdit')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const AvatarUploadCell = forwardRef<
  HTMLInputElement,
  Omit<AvatarUploadFieldProps, 't'>
>((props, ref) => {
  const { t } = useTranslation('myProfileEdit')
  return (
    <CellFrame sx={{ pb: 3 }}>
      <AvatarUploadField {...props} ref={ref} t={t} />
    </CellFrame>
  )
})

const defaultValues: MyProfileValues = {
  avatarPath: '',
  displayName: '',
  accountId: '',
  introduction: '',
}
